import {getConditionType} from 'common/src/modules/conditions/utils/getConditionType';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import {
  CreativeNode,
  DPACarouselCreativeNode,
  DPACreativeNode,
  DTCarouselNode,
  DTCompoundConditionNode,
  DTConditionNode,
  DTNode,
  DTOptimizationNode,
  DTScheduleNode,
  ProductMatchingOperator,
  ProductSelectionNodeOutputData,
  ProductSortingOrder,
  RemoteCompoundConditionNode,
  RemoteDPACarouselCreativeNode,
  RemoteDPACreativeNode,
  ServerCarouselNode,
  ServerConditionNode,
  ServerCreativeNode,
  ServerDTProductSelectionNode,
  ServerScheduleNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import {
  NodeInputData,
  NodeOutputData,
  NodeType,
  ServerNode,
} from 'common/src/modules/nodes/nodes.types';
import {ConditionOperatorKey, ConditionType} from 'common/src/modules/conditions/conditions.types';
import {FeedColumn} from 'common/src/modules/relatedItems/relatedItems.types';
import {ProductFeed} from 'common/src/modules/productFeed/productFeed.types';
import convertOutputsToClientModel from 'common/src/modules/nodes/utils/convertOutputsToClientModel';
import {DEFAULT_DROP_CONDITIONS} from '../../optimization/optimization.consts';

function conditionNodeToClientModel(
  node: ServerConditionNode,
  campaignEnums: Record<string, CLEnumsMapper>,
): DTConditionNode {
  const conditionType =
    node.condOperator === ConditionOperatorKey.Exists
      ? ConditionType.Boolean
      : getConditionType(node.condKeyType);

  return {
    ...node,
    categories: node.categories || [],
    condKeyTypeUiName:
      campaignEnums[CLEnumKeys.CampaignPage.AeScriptItemPropertyTypeEnum].byValue[node.condKeyType]
        .name,
    conditionType,
    outputs: _map(node.outputs, (output) => ({
      ...output,
      dateValues: output.dateValues ? output.dateValues.map((date) => DateTime.fromISO(date)) : [],
      numericValues: output.numericValues,
      booleanValue: output.booleanValue,
      textValues: output.textValues,
      datesRange: output.datesRange
        ? {
            from: DateTime.fromISO(output.datesRange.from),
            to: DateTime.fromISO(output.datesRange.to),
          }
        : null,
      numbersRange: output.numbersRange
        ? {from: +output.numbersRange.from, to: +output.numbersRange.to}
        : null,
    })),
  };
}

function compoundConditionNodeToClientModel(
  node: RemoteCompoundConditionNode,
): DTCompoundConditionNode {
  return {
    ...node,
    outputs: convertOutputsToClientModel(node.outputs),
  };
}

function scheduleNodeToClientModel(node: ServerScheduleNode): DTScheduleNode {
  return {
    ...node,
    outputs: _map(node.outputs, (output) => ({
      ...output,
      dateRange: output.dateRange
        ? {
            start: DateTime.fromISO(output.dateRange.start),
            end: DateTime.fromISO(output.dateRange.end),
          }
        : null,
    })),
  } as DTScheduleNode;
}

function productNodeToClientModel(
  node: ServerDTProductSelectionNode,
  relatedEnums: Record<string, CLEnumsMapper>,
): {
  outputs: NodeOutputData[] & ProductSelectionNodeOutputData[];
  categoryTopItemsLogicId: number;
  productsSelectionStrategy: number;
  configuration: {
    orderBy?: FeedColumn;
    selectionOrder?: ProductSortingOrder;
    conditions: {
      feedColumnSignalKey: string;
      operator: ProductMatchingOperator;
      signalKey: string;
      feedKey: number;
    }[];
    contentType: number;
  };
  productIds?: number[];
  inputs: NodeInputData[];
  nextOutputId?: number;
  description?: string;
  type: NodeType & NodeType.PRODUCTS;
  retargetingProductSetId: number;
  retargetingProductSetLogic: {items_count: number; name: string; id: number; itemsCount: number};
  feed?: ProductFeed;
  isFallbackCreative?: boolean;
  topItemLogicId: number;
  categoryTopItemsLogic: {items_count: number; name: string; id: number; itemsCount: number};
  feedId: number;
  recommendationSources?: number[];
  x?: number;
  name?: string;
  topItemLogic: {items_count: number; name: string; id: number; itemsCount: number};
  y?: number;
  id: number;
  teamNameSourceSignalKey?: string;
} {
  return {
    ...node,
    ...(node.topItemLogic && {
      topItemLogic: {
        ...node.topItemLogic,
        itemsCount: node.topItemLogic?.items_count ?? 0,
      },
    }),
    ...(node.categoryTopItemsLogic && {
      categoryTopItemsLogic: {
        ...node.categoryTopItemsLogic,
        itemsCount: node.categoryTopItemsLogic?.items_count ?? 0,
      },
    }),
    ...(node.retargetingProductSetLogic && {
      retargetingProductSetLogic: {
        ...node.retargetingProductSetLogic,
        itemsCount: node.retargetingProductSetLogic?.items_count ?? 0,
      },
    }),
    ...(node.configuration && {
      configuration: {
        ...node.configuration,

        orderBy:
          relatedEnums[CLEnumKeys.RelatedItems.FeedColumns].byName[node.configuration.orderBy]
            .value,
        conditions: _map(node.configuration.conditions, (condition) => {
          return {
            feedColumnSignalKey: condition.feedColumnSignalKey,
            operator: condition.operator,
            signalKey: condition.signalKey,
            feedKey: +FeedColumn[condition.feedKey as FeedColumn],
          };
        }),
      },
    }),
    ...(node.teamNameSourceSignalKey && {
      teamNameSourceSignalKey: node.teamNameSourceSignalKey,
    }),
  };
}

export function creativeNodeToClientModal(node: ServerCreativeNode): CreativeNode {
  return {
    ...node,
    ...(node.creative && {creative: node.creative}),
    creativeId: node.creativeId ?? node.creative?.id,
    outputs: [],
    images: node.images ?? [],
  };
}

export function carouselNodeToClientModal(node: ServerCarouselNode): DTCarouselNode {
  return {
    ...node,
    cardsInfo: _map(node.cardsInfo, (card) => ({
      ...card,
    })),
    outputs: [],
  };
}

export function DPANodeToClientModal(
  node: RemoteDPACreativeNode | RemoteDPACarouselCreativeNode,
): DPACarouselCreativeNode | DPACreativeNode {
  return {
    ...node,
    outputs: [],
  };
}

function ConvertOptimizationNode(optimizationNode: DTOptimizationNode): DTOptimizationNode {
  return {
    ...optimizationNode,
    configuration: {
      ...optimizationNode.configuration,
      dropConditions: optimizationNode.configuration.dropConditions || DEFAULT_DROP_CONDITIONS,
    },
  };
}

export function DTNodeToClientModel(
  node: ServerNode,
  campaignEnums: Record<string, CLEnumsMapper>,
  generalEnums: Record<string, CLEnumsMapper>,
  relatedEnums: Record<string, CLEnumsMapper>,
) {
  let newNode: DTNode = {...node, inputs: []};

  switch (node.type) {
    case NodeType.CONDITION:
      newNode = conditionNodeToClientModel(newNode as ServerConditionNode, campaignEnums);
      break;
    case NodeType.COMPOUND_CONDITION:
      newNode = compoundConditionNodeToClientModel(newNode as RemoteCompoundConditionNode);
      break;
    case NodeType.SCHEDULE:
      newNode = scheduleNodeToClientModel(newNode as ServerScheduleNode);
      break;
    case NodeType.PRODUCTS:
      newNode = productNodeToClientModel(newNode as ServerDTProductSelectionNode, relatedEnums);
      break;
    case NodeType.CREATIVE:
    case NodeType.NATIVE_CREATIVE:
    case NodeType.SOCIAL_FACEBOOK_AD_CREATIVE:
    case NodeType.SOCIAL_TIKTOK_AD_CREATIVE:
    case NodeType.SOCIAL_YOUTUBE_AD_CREATIVE:
    case NodeType.SOCIAL_YOUTUBE_DV360_AD_CREATIVE:
    case NodeType.SOCIAL_SNAPCHAT_AD_CREATIVE:
    case NodeType.SOCIAL_LINKEDIN_AD_CREATIVE:
      newNode = creativeNodeToClientModal(newNode as ServerCreativeNode);
      break;
    case NodeType.FACEBOOK_CAROUSEL_CREATIVE:
      newNode = carouselNodeToClientModal(newNode as ServerCarouselNode);
    case NodeType.FACEBOOK_DPA_CREATIVE:
    case NodeType.FACEBOOK_DPA_CAROUSEL_CREATIVE:
      newNode = DPANodeToClientModal(
        newNode as RemoteDPACreativeNode | RemoteDPACarouselCreativeNode,
      );
      break;
    case NodeType.GENERAL_OPTIMIZATION:
      return ConvertOptimizationNode(newNode as DTOptimizationNode);
    default:
      break;
  }

  return newNode;
}
